import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../store/authStore";
import { useWsStore } from "../store/wsStore";
import { useDevicesStore } from "../store/devicesStore";
import { refreshAccessToken } from "../api/axios";
import { clearClientSession } from "../store/session";

const RECONNECT_BASE = 1000;
const RECONNECT_MAX = 30_000;

export function useWebSocket() {
  const token = useAuthStore((s) => s.accessToken);
  const setConnected = useWsStore((s) => s.setConnected);
  const emit = useWsStore((s) => s.emit);
  const patchDevice = useDevicesStore((s) => s.patchDevice);
  const queryClient = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const tryRef = useRef(0);
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!token) return;
    stoppedRef.current = false;

    function connect() {
      if (stoppedRef.current) return;
      const wsProto = location.protocol === "https:" ? "wss:" : "ws:";
      const url = `${wsProto}//${location.host}/api/v1/ws?token=${encodeURIComponent(token!)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        tryRef.current = 0;
        setConnected(true);
        // Al conectar/reconectar el WS forzamos refetch del listado de devices
        // para que `is_online` quede sincronizado con la DB. Cubre el caso
        // "estaba desconectado, perdí los eventos device.status del backend".
        queryClient.invalidateQueries({ queryKey: ["devices"] });
      };

      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          emit(msg);
          if (msg.type === "device.status" && msg.payload) {
            const p = msg.payload;
            if (typeof p.device_id === "number") {
              patchDevice(p.device_id, { is_online: !!p.online });
            }
          }
        } catch {
          // ignore
        }
      };

      ws.onclose = async (ev) => {
        setConnected(false);
        if (stoppedRef.current) return;
        if (ev.code === 1008) {
          const refreshed = await refreshAccessToken();
          if (!refreshed) {
            clearClientSession();
            if (!window.location.pathname.startsWith("/login")) {
              window.location.assign("/login");
            }
          }
          return;
        }
        const delay = Math.min(RECONNECT_BASE * 2 ** tryRef.current, RECONNECT_MAX);
        tryRef.current += 1;
        setTimeout(connect, delay);
      };

      ws.onerror = () => {
        try { ws.close(); } catch {}
      };
    }

    connect();

    // ping cada 25s
    const ping = setInterval(() => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ type: "ping" })); } catch {}
      }
    }, 25_000);

    return () => {
      stoppedRef.current = true;
      clearInterval(ping);
      try { wsRef.current?.close(); } catch {}
      wsRef.current = null;
      setConnected(false);
    };
  }, [token, setConnected, emit, patchDevice, queryClient]);
}
